import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PUBLIC_ROUTES } from "@/routes/url";
import { getAuthToken } from "@/utils/local-storage.utils";
import { IS_DEVELOPMENT } from "@/config/env.config";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  homePath?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  private handleGoHome = () => {
    // Full reload, not navigate() — the router tree is the thing that just crashed.
    window.location.href =
      this.props.homePath ?? (getAuthToken() ? PUBLIC_ROUTES.OVERVIEW : PUBLIC_ROUTES.LOGIN);
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return <>{this.props.fallback}</>;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center font-dashboard">
        <span className="mb-4 flex size-11 items-center justify-center rounded-xl bg-dash-hover text-dash-rust" aria-hidden="true">
          <AlertTriangle className="size-5" />
        </span>
        <h2 className="text-xl font-semibold text-dash-ink">Something went wrong</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-dash-muted">
          This page hit an unexpected error. Reloading usually clears it.
        </p>
        {IS_DEVELOPMENT && (
          <pre className="mt-4 max-w-lg overflow-x-auto rounded-xl bg-dash-chip p-3 text-left text-xs text-dash-muted">
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex gap-3">
          <Button onClick={() => window.location.reload()} data-testid="error-boundary-reload">
            Reload page
          </Button>
          <Button variant="outline" onClick={this.handleGoHome} data-testid="error-boundary-home">
            Go home
          </Button>
        </div>
      </div>
    );
  }
}
